import React, { useState, useEffect } from 'react';
import { hookEnrollmentApi, liveInstructionApi, studentProgressApi, tasksApi } from '../../services/api';

interface LearningHook {
  id: number;
  title: string;
  description: string;
  grade_levels: string;
  project_type: string;
  max_students: number;
  enrolled_count: number;
  teacher_name: string;
}

interface HookEnrollment {
  id: number;
  learning_hook: number;
  learning_hook_title: string;
  status: string;
  enrolled_at: string;
  progress_percentage: number;
  current_stage: string | null;
  progress_id: number | null;
}

interface Task {
  id: number;
  learning_hook: number;
  stage: string;
  title: string;
  instructions: string;
  rubric: string;
  accepted_evidence_types?: string[];
  google_template_file_id?: string | null;
  google_template_name?: string | null;
  google_template_mime_type?: string | null;
  my_evidence?: {
    id: number;
    status: string;
    submission_text: string;
    submitted_at: string | null;
    reviewed_at: string | null;
    reviewed_by: number | null;
    latest_feedback_decision: string | null;
    latest_feedback_notes: string;
    latest_feedback_at: string | null;
  } | null;
  order: number;
  is_required: boolean;
  is_active: boolean;
}

interface TaskArtifact {
  id: number;
  task: number;
  student: number;
  drive_file_id: string;
  drive_url: string;
  drive_name?: string | null;
  drive_mime_type?: string | null;
  status: string;
  created_at: string | null;
}

interface LiveArtifact {
  id: number;
  kind: string;
  content: string;
  links?: any[];
  published_state: 'draft' | 'published';
}

interface LiveSession {
  id: number;
  learning_hook: number;
  level: string;
  mode: string;
  status: string;
  title?: string;
  instruction_source_type?: string | null;
  instruction_source_id?: string | null;
  follow_mode?: string | null;
  artifacts?: LiveArtifact[];
}

export const HookEnrollment: React.FC = () => {
  const [myEnrollments, setMyEnrollments] = useState<HookEnrollment[]>([]);
  const [availableHooks, setAvailableHooks] = useState<LearningHook[]>([]);
  const [tasksByHookId, setTasksByHookId] = useState<Record<number, Task[]>>({});
  const [evidenceDraftByTaskId, setEvidenceDraftByTaskId] = useState<Record<number, string>>({});
  const [artifactByTaskId, setArtifactByTaskId] = useState<Record<number, TaskArtifact>>({});
  const [liveSessionByHookId, setLiveSessionByHookId] = useState<Record<number, LiveSession | null>>({});
  const [recentEndedSessionByHookId, setRecentEndedSessionByHookId] = useState<Record<number, LiveSession | null>>({});
  const [liveBehindByHookId, setLiveBehindByHookId] = useState<Record<number, boolean>>({});
  const [openLiveHookId, setOpenLiveHookId] = useState<number | null>(null);
  const [activeTabByHookId, setActiveTabByHookId] = useState<Record<number, 'instruction' | 'work'>>({});
  const [sendingSignalHookId, setSendingSignalHookId] = useState<number | null>(null);
  const [sendingSignalType, setSendingSignalType] = useState<string>('');
  const [loadingArtifactTaskId, setLoadingArtifactTaskId] = useState<number | null>(null);
  const [submittingTaskId, setSubmittingTaskId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const stageOrder = ['why', 'define', 'do', 'explain', 'apply', 'reflect', 'completed'];
  const stageLabel = (stage: string) => {
    if (!stage) return '';
    return stage.charAt(0).toUpperCase() + stage.slice(1);
  };

  const handleSendSignal = async (hookId: number, sessionId: number, signalType: 'good' | 'confused' | 'another_example' | 'slow_down') => {
    setError('');
    setSuccess('');
    try {
      setSendingSignalHookId(hookId);
      setSendingSignalType(signalType);
      await liveInstructionApi.postSignal(sessionId, { signal_type: signalType });
      setSuccess('Sent');
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to send signal');
    } finally {
      setSendingSignalHookId(null);
      setSendingSignalType('');
    }
  };

  const handleOpenOrCreateDoc = async (task: Task) => {
    setError('');
    setSuccess('');

    const existing = artifactByTaskId[task.id];
    if (existing?.drive_url) {
      window.open(existing.drive_url, '_blank', 'noopener,noreferrer');
      return;
    }

    try {
      setLoadingArtifactTaskId(task.id);
      const res = await tasksApi.getArtifact(task.id);
      const artifact = res?.data as TaskArtifact;
      if (!artifact?.drive_url) {
        setError('Failed to create or load your document');
        return;
      }

      setArtifactByTaskId((prev: Record<number, TaskArtifact>) => ({ ...(prev || {}), [task.id]: artifact }));

      setEvidenceDraftByTaskId((prev: Record<number, string>) => {
        const curr = (prev || {}) as Record<number, string>;
        const existingText = (curr[task.id] || '').trim();
        if (existingText) return curr;
        return { ...curr, [task.id]: artifact.drive_url };
      });

      window.open(artifact.drive_url, '_blank', 'noopener,noreferrer');
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to create your document');
    } finally {
      setLoadingArtifactTaskId(null);
    }
  };

  const getNextStage = (currentStage: string | null) => {
    if (!currentStage) return null;
    const idx = stageOrder.indexOf(currentStage);
    if (idx < 0) return null;
    if (idx >= stageOrder.length - 1) return null;
    return stageOrder[idx + 1];
  };

  const getStageGateState = (enrollment: HookEnrollment) => {
    const hookId = enrollment.learning_hook;
    const tasks = hookId ? (tasksByHookId[hookId] || []) : [];
    const gateTasks = tasks.filter((t: Task) => t.is_active && t.is_required);
    if (gateTasks.length === 0) {
      return { locked: false, gateTasks };
    }
    const hasApproved = gateTasks.some((t: Task) => t.my_evidence?.status === 'approved');
    return { locked: !hasApproved, gateTasks };
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [enrollmentsResponse, hooksResponse] = await Promise.all([
        hookEnrollmentApi.getMyEnrollments(),
        hookEnrollmentApi.getAvailableHooks()
      ]);
      
      const enrollments: HookEnrollment[] = enrollmentsResponse.data || [];
      setMyEnrollments(enrollments);
      setAvailableHooks(hooksResponse.data);

      const active = (Array.isArray(enrollments) ? enrollments : []).filter((e: HookEnrollment) => e.status === 'active');
      const taskResults = await Promise.all(active.map(async (e: HookEnrollment) => {
        if (!e.learning_hook || !e.current_stage) {
          return { hookId: e.learning_hook, tasks: [] as Task[] };
        }
        const res = await tasksApi.list({ hook_id: e.learning_hook, stage: e.current_stage });
        return { hookId: e.learning_hook, tasks: (res.data?.tasks || []) as Task[] };
      }));

      const liveResults = await Promise.all(
        active.map(async (e: HookEnrollment) => {
          try {
            const hookId = e.learning_hook;
            if (!hookId) return { hookId, session: null as LiveSession | null, behind: false };
            const res = await liveInstructionApi.getHookSession(hookId);
            const session = (res?.data?.session || null) as LiveSession | null;
            const recentEnded = (res?.data?.recent_ended_session || null) as LiveSession | null;
            const behind = !!res?.data?.student_is_behind_live_level;
            return { hookId, session, recentEnded, behind };
          } catch (err) {
            return { hookId: e.learning_hook, session: null as LiveSession | null, recentEnded: null as LiveSession | null, behind: false };
          }
        })
      );

      const nextMap: Record<number, Task[]> = {};
      for (const r of taskResults) {
        if (r.hookId) nextMap[r.hookId] = r.tasks;
      }
      setTasksByHookId(nextMap);

      const nextLive: Record<number, LiveSession | null> = {};
      const nextEnded: Record<number, LiveSession | null> = {};
      const nextBehind: Record<number, boolean> = {};
      for (const r of liveResults) {
        if (r.hookId) {
          nextLive[r.hookId] = r.session;
          nextEnded[r.hookId] = (r as any).recentEnded || null;
          nextBehind[r.hookId] = !!r.behind;
        }
      }
      setLiveSessionByHookId(nextLive);
      setRecentEndedSessionByHookId(nextEnded);
      setLiveBehindByHookId(nextBehind);

      setActiveTabByHookId((prev) => {
        const next = { ...(prev || {}) } as Record<number, 'instruction' | 'work'>;
        for (const e of active) {
          const hookId = e.learning_hook;
          if (!hookId) continue;
          if (next[hookId]) continue;
          next[hookId] = nextLive[hookId] && nextLive[hookId]?.status === 'active' ? 'instruction' : 'work';
        }
        return next;
      });

      setEvidenceDraftByTaskId((prev: Record<number, string>) => {
        const next = { ...(prev || {}) } as Record<number, string>;
        for (const hookIdStr of Object.keys(nextMap)) {
          const hookId = Number(hookIdStr);
          const tasks = nextMap[hookId] || [];
          for (const t of tasks) {
            const existingDraft = next[t.id];
            if (existingDraft != null) continue;
            const submittedText = (t.my_evidence && t.my_evidence.submission_text) ? t.my_evidence.submission_text : '';
            if (submittedText) {
              next[t.id] = submittedText;
            }
          }
        }
        return next;
      });
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to load learning hooks');
    } finally {
      setLoading(false);
    }
  };

  const handleAdvanceStage = async (enrollment: HookEnrollment, nextStage: string) => {
    setError('');
    setSuccess('');

    if (!enrollment.progress_id) {
      setError('Progress record not available yet. Please refresh and try again.');
      return;
    }

    try {
      await studentProgressApi.updateStage(enrollment.progress_id, nextStage);
      setSuccess(`Moved to ${stageLabel(nextStage)}.`);
      fetchData();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to advance stage');
    }
  };

  const handleAdvanceToDefine = async (enrollment: HookEnrollment) => {
    setError('');
    setSuccess('');

    if (!enrollment.progress_id) {
      setError('Progress record not available yet. Please refresh and try again.');
      return;
    }

    try {
      await studentProgressApi.updateStage(enrollment.progress_id, 'define');
      setSuccess('Moved to Define.');
      fetchData();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to advance stage');
    }
  };

  const handleSubmitEvidence = async (taskId: number) => {
    setError('');
    setSuccess('');
    const submission_text = (evidenceDraftByTaskId[taskId] || '').trim();
    if (!submission_text) {
      setError('Please enter evidence before submitting.');
      return;
    }

    try {
      setSubmittingTaskId(taskId);
      const res = await tasksApi.submitEvidence(taskId, { submission_text });
      const evidenceId = res?.data?.id;
      setSuccess('Evidence submitted');

      setTasksByHookId((prev: Record<number, Task[]>) => {
        const next: Record<number, Task[]> = { ...(prev || {}) };
        for (const hookIdStr of Object.keys(next)) {
          const hookId = Number(hookIdStr);
          const tasks = next[hookId] || [];
          if (tasks.find((t) => t.id === taskId)) {
            next[hookId] = tasks.map((t) => {
              if (t.id !== taskId) return t;
              return {
                ...t,
                my_evidence: {
                  id: (typeof evidenceId === 'number' ? evidenceId : (t.my_evidence?.id || 0)),
                  status: 'submitted',
                  submission_text,
                  submitted_at: new Date().toISOString(),
                  reviewed_at: null,
                  reviewed_by: null,
                  latest_feedback_decision: null,
                  latest_feedback_notes: '',
                  latest_feedback_at: null,
                },
              };
            });
            break;
          }
        }
        return next;
      });
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to submit evidence');
    } finally {
      setSubmittingTaskId(null);
    }
  };

  const handleEnroll = async (hookId: number) => {
    setError('');
    setSuccess('');
    
    try {
      await hookEnrollmentApi.enrollInHook(hookId);
      setSuccess('Successfully enrolled in learning hook!');
      fetchData(); // Refresh data
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to enroll in learning hook');
    }
  };

  if (loading) return <div className="p-6">Loading learning hooks...</div>;

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">My Learning Hooks</h2>
      
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}
      
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          {success}
        </div>
      )}

      {/* Current Enrollments */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-4">Active Enrollments</h3>
        {myEnrollments.length === 0 ? (
          <p className="text-gray-500">You are not enrolled in any learning hooks yet.</p>
        ) : (
          <div className="grid gap-4">
            {myEnrollments.map((enrollment: HookEnrollment) => (
              <div key={enrollment.id} className="border rounded-lg p-4">
                <h4 className="font-medium text-lg">{enrollment.learning_hook_title}</h4>
                <div className="text-sm text-gray-600 mt-1">
                  Stage: <span className="font-medium">{enrollment.current_stage || '—'}</span>
                </div>

                {(() => {
                  const hookId = enrollment.learning_hook;
                  if (!hookId) return null;
                  const live = liveSessionByHookId[hookId];
                  const ended = recentEndedSessionByHookId[hookId];
                  const tab = activeTabByHookId[hookId] || (live && live.status === 'active' ? 'instruction' : 'work');
                  const behind = !!liveBehindByHookId[hookId];
                  const isHighlightsOpen = openLiveHookId === hookId;

                  const artifacts = (live && live.status === 'active' ? (live.artifacts || []) : (ended?.artifacts || [])) as LiveArtifact[];
                  const hasSlides = !!(live && live.status === 'active' && (live.instruction_source_id || ''));
                  const slidesUrl = hasSlides ? `https://docs.google.com/presentation/d/${live?.instruction_source_id}/preview` : '';
                  const slidesDriveUrl = hasSlides ? `https://docs.google.com/presentation/d/${live?.instruction_source_id}/edit` : '';

                  return (
                    <div className="mt-4">
                      <div className="border-b flex gap-4">
                        <button
                          type="button"
                          onClick={() => setActiveTabByHookId((prev) => ({ ...(prev || {}), [hookId]: 'instruction' }))}
                          className={`py-2 text-sm font-medium ${tab === 'instruction' ? 'border-b-2 border-purple-600 text-purple-700' : 'text-gray-600 hover:text-gray-800'}`}
                        >
                          Instruction
                        </button>
                        <button
                          type="button"
                          onClick={() => setActiveTabByHookId((prev) => ({ ...(prev || {}), [hookId]: 'work' }))}
                          className={`py-2 text-sm font-medium ${tab === 'work' ? 'border-b-2 border-blue-600 text-blue-700' : 'text-gray-600 hover:text-gray-800'}`}
                        >
                          Work
                        </button>
                      </div>

                      {tab === 'instruction' ? (
                        <div className="mt-3 rounded border border-purple-200 bg-purple-50 p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="text-sm text-purple-900">
                              {live && live.status === 'active' ? (
                                <div className="font-medium">
                                  Live now: {stageLabel(live.level || '')} — {(live.mode || '').replace(/_/g, ' ')}
                                </div>
                              ) : (
                                <div className="font-medium">Not live</div>
                              )}
                              {live && live.status === 'active' && behind && (
                                <div className="text-xs text-purple-800 mt-1">
                                  You’re previewing live instruction. This does not unlock progress.
                                </div>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => setOpenLiveHookId(isHighlightsOpen ? null : hookId)}
                              className="bg-white border px-2 py-1 rounded text-xs hover:bg-gray-50"
                            >
                              {isHighlightsOpen ? 'Hide highlights' : 'View highlights'}
                            </button>
                          </div>

                          {live && live.status === 'active' ? (
                            <div className="mt-3">
                              <div className="text-xs text-purple-900 font-medium">Slides</div>
                              {hasSlides ? (
                                <div className="mt-2">
                                  <div className="flex items-center justify-between">
                                    <a href={slidesDriveUrl} target="_blank" rel="noreferrer" className="text-xs text-purple-900 underline">
                                      Open in Drive
                                    </a>
                                  </div>
                                  <div className="mt-2 border rounded overflow-hidden bg-white" style={{ height: 320 }}>
                                    <iframe title="Slides" src={slidesUrl} className="w-full h-full" allow="autoplay" />
                                  </div>
                                </div>
                              ) : (
                                <div className="text-xs text-purple-800 mt-1">Slides aren’t attached yet.</div>
                              )}
                            </div>
                          ) : null}

                          {live && live.status === 'active' ? (
                            <div className="mt-4">
                              <div className="text-xs text-purple-900 font-medium">Signals</div>
                              <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleSendSignal(hookId, live.id, 'good')}
                                  disabled={sendingSignalHookId === hookId}
                                  className="bg-white border px-2 py-2 rounded text-xs hover:bg-gray-50 disabled:opacity-50"
                                >
                                  {sendingSignalHookId === hookId && sendingSignalType === 'good' ? 'Sending…' : '👍 I’m good'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleSendSignal(hookId, live.id, 'confused')}
                                  disabled={sendingSignalHookId === hookId}
                                  className="bg-white border px-2 py-2 rounded text-xs hover:bg-gray-50 disabled:opacity-50"
                                >
                                  {sendingSignalHookId === hookId && sendingSignalType === 'confused' ? 'Sending…' : '❓ I’m confused'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleSendSignal(hookId, live.id, 'another_example')}
                                  disabled={sendingSignalHookId === hookId}
                                  className="bg-white border px-2 py-2 rounded text-xs hover:bg-gray-50 disabled:opacity-50"
                                >
                                  {sendingSignalHookId === hookId && sendingSignalType === 'another_example' ? 'Sending…' : '🔁 Another example'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleSendSignal(hookId, live.id, 'slow_down')}
                                  disabled={sendingSignalHookId === hookId}
                                  className="bg-white border px-2 py-2 rounded text-xs hover:bg-gray-50 disabled:opacity-50"
                                >
                                  {sendingSignalHookId === hookId && sendingSignalType === 'slow_down' ? 'Sending…' : '⏸ Slow down'}
                                </button>
                              </div>
                            </div>
                          ) : null}

                          {isHighlightsOpen && (
                            <div className="mt-4 space-y-2">
                              {artifacts.length === 0 ? (
                                <div className="text-xs text-purple-800">No published highlights yet.</div>
                              ) : (
                                artifacts.map((a: LiveArtifact) => (
                                  <div key={a.id} className="bg-white border rounded p-2">
                                    <div className="text-xs font-medium text-gray-800">{(a.kind || '').replace(/_/g, ' ')}</div>
                                    <div className="text-xs text-gray-700 mt-1 whitespace-pre-wrap">{a.content || '—'}</div>
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      ) : null}

                      {tab === 'work' ? (
                        <div>
                          <div className="mt-2">
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-sm text-gray-500">Progress</span>
                              <span className="text-sm font-medium">{enrollment.progress_percentage}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-blue-500 h-2 rounded-full" 
                                style={{ width: `${enrollment.progress_percentage}%` }}
                              ></div>
                            </div>
                          </div>
                          <p className="text-sm text-gray-500 mt-2">
                            Enrolled: {new Date(enrollment.enrolled_at).toLocaleDateString()}
                          </p>

                          <div className="mt-4">
                            <h5 className="text-sm font-semibold text-gray-900 mb-2">Tasks for this stage</h5>
                            {enrollment.current_stage === 'why' && (
                              <div className="mb-3 rounded border bg-blue-50 border-blue-200 p-3 text-sm text-blue-900">
                                <div className="font-medium">Why stage</div>
                                <div className="mt-1 text-blue-800">
                                  This stage is an introduction to why this project matters. When you’re ready, continue to the Define stage to begin the required tasks.
                                </div>
                                {(() => {
                                  const { locked, gateTasks } = getStageGateState(enrollment);
                                  return locked ? (
                                    <div className="mt-2 text-xs text-blue-900">
                                      You can’t advance yet. A required assessment must be approved first:
                                      <div className="mt-1">
                                        {gateTasks.map((t: Task) => t.title).join(', ')}
                                      </div>
                                    </div>
                                  ) : null;
                                })()}
                                <div className="mt-2 flex justify-end">
                                  <button
                                    onClick={() => handleAdvanceToDefine(enrollment)}
                                    className="bg-blue-600 text-white px-3 py-1.5 rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                                    disabled={getStageGateState(enrollment).locked}
                                  >
                                    Continue to Define
                                  </button>
                                </div>
                              </div>
                            )}

                            {enrollment.current_stage && enrollment.current_stage !== 'completed' && enrollment.current_stage !== 'why' && (
                              (() => {
                                const nextStage = getNextStage(enrollment.current_stage);
                                if (!nextStage) return null;
                                const { locked, gateTasks } = getStageGateState(enrollment);
                                return (
                                  <div className="mb-3 rounded border bg-gray-50 border-gray-200 p-3 text-sm text-gray-900">
                                    <div className="font-medium">Next stage: {stageLabel(nextStage)}</div>
                                    {gateTasks.length > 0 ? (
                                      <div className="mt-1 text-xs text-gray-700">
                                        To move on, you need at least one required assessment for this stage approved. Practice tasks are optional.
                                      </div>
                                    ) : (
                                      <div className="mt-1 text-xs text-gray-700">
                                        No required assessments are set for this stage. You can continue.
                                      </div>
                                    )}
                                    {locked && (
                                      <div className="mt-2 text-xs text-yellow-800">
                                        Locked until approved: {gateTasks.map((t: Task) => t.title).join(', ')}
                                      </div>
                                    )}
                                    <div className="mt-2 flex justify-end">
                                      <button
                                        onClick={() => handleAdvanceStage(enrollment, nextStage)}
                                        className="bg-blue-600 text-white px-3 py-1.5 rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                                        disabled={locked}
                                      >
                                        Continue to {stageLabel(nextStage)}
                                      </button>
                                    </div>
                                  </div>
                                );
                              })()
                            )}

                            {enrollment.learning_hook && enrollment.current_stage ? (
                              (tasksByHookId[enrollment.learning_hook] || []).length === 0 ? (
                                <div className="text-sm text-gray-500">No tasks for this stage.</div>
                              ) : (
                                <div className="space-y-3">
                                  {(tasksByHookId[enrollment.learning_hook] || [])
                                    .slice()
                                    .sort((a: Task, b: Task) => (a.order ?? 0) - (b.order ?? 0))
                                    .map((t: Task) => (
                                      <div key={t.id} className="border rounded p-3 bg-gray-50">
                                        <div className="flex items-center justify-between">
                                          <div className="font-medium text-sm">{t.title}</div>
                                          <div className="text-xs text-gray-500">#{t.id} • {t.is_required ? 'required' : 'optional'}</div>
                                        </div>
                                        {t.instructions && (
                                          <div className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{t.instructions}</div>
                                        )}
                                        <div className="text-xs text-gray-500 mt-1">
                                          Accepted evidence: {(t.accepted_evidence_types && t.accepted_evidence_types.length > 0 ? t.accepted_evidence_types : ['text']).join(', ')}
                                        </div>

                                        {(t.accepted_evidence_types || []).indexOf('google_drive') !== -1 && (t.google_template_file_id || '') && (
                                          <div className="mt-2 flex items-center justify-between gap-3">
                                            <div className="text-xs text-gray-700 truncate">
                                              {artifactByTaskId[t.id]?.drive_url ? (
                                                <a
                                                  href={artifactByTaskId[t.id].drive_url}
                                                  target="_blank"
                                                  rel="noreferrer"
                                                  className="text-blue-700 underline"
                                                >
                                                  Your document
                                                </a>
                                              ) : (
                                                <span className="text-gray-700">Template: {t.google_template_name || 'Google Drive'}</span>
                                              )}
                                            </div>
                                            <button
                                              type="button"
                                              onClick={() => handleOpenOrCreateDoc(t)}
                                              disabled={loadingArtifactTaskId === t.id}
                                              className="bg-white border px-2 py-1 rounded text-xs hover:bg-gray-50 disabled:opacity-50"
                                            >
                                              {loadingArtifactTaskId === t.id ? 'Opening…' : (artifactByTaskId[t.id]?.drive_url ? 'Open' : 'Create')}
                                            </button>
                                          </div>
                                        )}

                                        {t.my_evidence?.status && (
                                          <div className="text-xs text-gray-600 mt-1">
                                            Status: <span className="font-medium">{t.my_evidence.status}</span>
                                          </div>
                                        )}

                                        {t.my_evidence?.status === 'needs_revision' && (t.my_evidence.latest_feedback_notes || '') && (
                                          <div className="text-xs text-yellow-800 mt-1 whitespace-pre-wrap">
                                            Teacher feedback: {t.my_evidence.latest_feedback_notes}
                                          </div>
                                        )}

                                        <div className="mt-2">
                                          <textarea
                                            value={evidenceDraftByTaskId[t.id] || ''}
                                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                                              setEvidenceDraftByTaskId((prev: Record<number, string>) => ({ ...prev, [t.id]: e.target.value }))
                                            }
                                            rows={3}
                                            className="w-full border rounded px-3 py-2 text-sm"
                                            placeholder="Paste a link, describe your work, or submit your evidence here..."
                                          />
                                          <div className="mt-2 flex justify-end">
                                            <button
                                              onClick={() => handleSubmitEvidence(t.id)}
                                              className="bg-blue-600 text-white px-3 py-1.5 rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                                              disabled={
                                                submittingTaskId === t.id ||
                                                (t.my_evidence?.status
                                                  ? (t.my_evidence.status !== 'needs_revision' && t.my_evidence.status !== 'draft')
                                                  : false)
                                              }
                                            >
                                              {submittingTaskId === t.id ? 'Submitting…' : 'Submit evidence'}
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              )
                            ) : (
                              <div className="text-sm text-gray-500">Stage not available yet.</div>
                            )}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Available Hooks */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Available Learning Hooks</h3>
        {availableHooks.length === 0 ? (
          <p className="text-gray-500">No new learning hooks available for your grade level.</p>
        ) : (
          <div className="grid gap-4">
            {availableHooks.map((hook) => (
              <div key={hook.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                <h4 className="font-medium text-lg">{hook.title}</h4>
                <p className="text-gray-600 text-sm mt-1">{hook.description}</p>
                
                <div className="mt-3 flex flex-wrap gap-2 text-sm">
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                    {hook.grade_levels || 'All Grades'}
                  </span>
                  <span className="px-2 py-1 bg-green-100 text-green-700 rounded">
                    {hook.project_type}
                  </span>
                  {hook.max_students && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">
                      {hook.enrolled_count}/{hook.max_students} enrolled
                    </span>
                  )}
                </div>
                
                <div className="mt-3 flex justify-between items-center">
                  <span className="text-sm text-gray-500">Teacher: {hook.teacher_name}</span>
                  <button
                    onClick={() => handleEnroll(hook.id)}
                    className="bg-blue-500 text-white px-4 py-2 rounded text-sm hover:bg-blue-600"
                  >
                    Enroll
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
